import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Send, RotateCcw, Check } from "lucide-react";
import { PLATFORM_LABELS } from "@/lib/constants";

const SUGGESTIONS = ["Punchier hook", "Cut the hype", "Stronger CTA", "Shorter", "More like me"];

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Types `text` out once (frontend reveal). Honors reduced-motion. */
function Typed({ text, speed = 12 }) {
  const [n, setN] = useState(() => (prefersReducedMotion() ? (text?.length || 0) : 0));
  useEffect(() => {
    if (prefersReducedMotion()) { setN(text?.length || 0); return; }
    setN(0);
    if (!text) return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= text.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [text, speed]);
  return <>{text ? text.slice(0, n) : ""}</>;
}

/**
 * Docked revise agent (worklist #22 / docs/v3-mockups/revise-in-create.html).
 * Lives on the right of the run review, acting on the SELECTED draft. Revise and
 * regenerate ("Fresh take") are both first-class; each turn is a real
 * reviseAsset/regenerate call (which updates the draft card on the left).
 */
export function ReviseAgentPanel({ asset, onRevise, onRegenerate, onApprove, className }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const scrollRef = useRef(null);

  const platformLabel = useMemo(
    () => PLATFORM_LABELS?.[asset?.platform] || asset?.platform || "post",
    [asset?.platform],
  );

  // Reset the conversation whenever the operator points the agent at a new card.
  useEffect(() => {
    if (!asset) return;
    setMessages([{ role: "agent", text: `Here's your ${platformLabel} draft. Tell me what to tighten — or tap a suggestion.` }]);
    setInput("");
  }, [asset?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const isApproved = asset?.status === "approved" || asset?.status === "scheduled" || asset?.status === "published";

  const runTurn = async (instruction, mode) => {
    if (running || !asset) return;
    const text = (instruction || "").trim();
    if (mode === "revise" && !text) return;
    setRunning(true);
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", text: mode === "regenerate" ? "Fresh take — a new angle on the same idea." : text },
      { role: "agent", text: "Working…", pending: true },
    ]);
    let ok = true;
    try {
      const res = mode === "regenerate" ? await onRegenerate?.(text) : await onRevise?.(text);
      ok = res?.ok !== false;
    } catch {
      ok = false;
    }
    setMessages((m) => {
      const kept = m.filter((x) => !x.pending);
      kept.push({
        role: "agent",
        text: ok
          ? mode === "regenerate"
            ? "Fresh take ready — the new draft is on the left."
            : "Done — I tightened it. The updated draft is on the left."
          : "That didn't go through. Want to try again?",
      });
      return kept;
    });
    setRunning(false);
  };

  const approve = () => {
    onApprove?.();
    setMessages((m) => [...m, { role: "agent", text: "Approved — moved to your review queue for scheduling." }]);
  };

  return (
    <div
      className={cn("flex flex-col overflow-hidden rounded-2xl border bg-card", className)}
      style={{ borderColor: "var(--border-subtle)" }}
      data-testid="revise-agent-panel"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
        <span
          className="agent-orb h-7 w-7 shrink-0 rounded-full"
          style={{ background: "radial-gradient(circle at 35% 30%, var(--accent-cyan), var(--accent-blue))" }}
          aria-hidden
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-1">Agent</p>
          <p className="truncate text-[11.5px] text-muted-foreground">Refining your {platformLabel} post</p>
        </div>
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="min-h-[220px] flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-4" data-testid="revise-agent-log">
        {messages.map((m, i) => (
          <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div
              className={cn(
                "max-w-[92%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed",
                m.role === "user"
                  ? "bg-[color:var(--surface-2)] text-1"
                  : "border-l-2 border-[color:var(--accent-blue)] bg-[color:var(--accent-blue)]/10 text-1",
              )}
            >
              {m.role === "agent" && !m.pending ? <Typed text={m.text} /> : m.text}
              {m.pending ? <span className="ml-1 inline-flex gap-0.5 align-middle"><Dot /><Dot d={150} /><Dot d={300} /></span> : null}
            </div>
          </div>
        ))}
      </div>

      {/* Suggestion chips */}
      <div className="flex flex-wrap gap-1.5 px-4 pb-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={running || isApproved}
            onClick={() => runTurn(s, "revise")}
            className="rounded-full border bg-[color:var(--surface-2)] px-2.5 py-1 text-[11px] text-[color:var(--text-2)] transition-colors hover:border-[color:var(--accent-blue)]/50 hover:text-1 disabled:opacity-50"
            style={{ borderColor: "var(--border-subtle)" }}
            data-testid={`revise-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Composer */}
      <div className="flex items-end gap-2 px-4 pb-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); runTurn(input, "revise"); }
          }}
          disabled={running || isApproved}
          rows={1}
          placeholder="Tell the agent what to change…"
          className="max-h-28 min-h-[40px] flex-1 resize-none rounded-lg border bg-[color:var(--surface-2)] px-3 py-2.5 text-[12.5px] text-1 outline-none placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-[color:var(--accent-cyan)]/40 disabled:opacity-50"
          style={{ borderColor: "var(--border-subtle)" }}
          data-testid="revise-agent-input"
        />
        <button
          type="button"
          onClick={() => runTurn(input, "revise")}
          disabled={running || isApproved || !input.trim()}
          aria-label="Send"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color:var(--accent-blue)] text-[color:var(--accent-blue-ink)] transition hover:opacity-90 disabled:opacity-40"
          data-testid="revise-agent-send"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      {/* Fresh take + Approve */}
      <div className="flex items-center gap-2 border-t px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
        <button
          type="button"
          onClick={() => runTurn("", "regenerate")}
          disabled={running || isApproved}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border bg-[color:var(--surface-2)] px-3 py-2 text-[12.5px] font-medium text-1 transition-colors hover:bg-[color:var(--surface-3)] disabled:opacity-50"
          style={{ borderColor: "var(--border-subtle)" }}
          data-testid="revise-agent-fresh-take"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Fresh take
        </button>
        <button
          type="button"
          onClick={approve}
          disabled={isApproved}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[color:var(--accent-green)]/45 bg-[color:var(--accent-green)]/12 px-3 py-2 text-[12.5px] font-semibold text-[color:var(--accent-green)] transition-colors hover:bg-[color:var(--accent-green)]/20 disabled:opacity-50"
          data-testid="revise-agent-approve"
        >
          <Check className="h-3.5 w-3.5" /> {isApproved ? "Approved" : "Approve"}
        </button>
      </div>
    </div>
  );
}

function Dot({ d = 0 }) {
  return (
    <span
      className="inline-block h-1 w-1 animate-bounce rounded-full bg-current opacity-70"
      style={{ animationDelay: `${d}ms` }}
    />
  );
}
